/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartStatTiles } from '../ChartStatTiles';
import { ChartEmptyState } from '../ChartEmptyState';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            padding: '0 15px',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        barTitleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {
                display: 'flex',
                alignItems: 'center',
            },
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
    }),
);

export enum LastCountEnum {
    LAST_WEEK,
    LAST_14_DAYS,
    LAST_30_DAYS,
    LAST_12_MOTNHS,
}

interface TimeRecordedInPeriodChartProps extends ChartCommonProps {
    getSelectedItem?: (searchCriterias: Array<SearchCriteria>, isShowCompleted: boolean, title?: string, drillDownType?: DrillDownType, defaultView?: string) => void;
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    billed: number[];
    unbilled: number[];
    valueAmounts: number[];
    billedTotal: number;
    unbilledTotal: number;
    currentTotal: number;
    previousTotal: number;
    periodString: string;
    previousPeroidString: string;
    symbol: string;
}

interface TimeFeesData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    timeRecordedInPeriod: TimeRecordedInPeriod;
}

export interface TimeRecordedInPeriod {
    last14Days: Last;
    last30Days: Last;
    last12Months: Last;
    lastWeek: Last;
}

export interface Last {
    lastPeriodTotals: LastPeriodTotals;
    summary: Summary;
    timesheetTimeGrouping: TimesheetTimeGrouping[];
}

export interface LastPeriodTotals {
    periodTotal: number;
    billedTotal: number;
    unbilledTotal: number;
}

export interface Summary {
    lastPeriodTotal: number;
}

export interface TimesheetTimeGrouping {
    billed: number;
    unbilled: number;
    group: string;
    valueAmount: number;
}

export const TimeRecordedInPeriodChart: React.FC<TimeRecordedInPeriodChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line: no-any
    const chartRef = React.useRef<any>();

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.LAST_WEEK,
        months: [],
        billed: [],
        unbilled: [],
        valueAmounts: [],
        billedTotal: 0,
        unbilledTotal: 0,
        currentTotal: 0,
        previousTotal: 0,
        periodString: 'This Week',
        previousPeroidString: 'Week',
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeFeesData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let billed: number[] = [];
                let unbilled: number[] = [];
                let valueAmounts: number[] = [];
                let previousTotal: number = 0;
                let currentTotal: number = 0;
                let billedTotal: number = 0;
                let unbilledTotal: number = 0;
                let periodString: string = '';
                let previousPeroidString: string = '';
                if (!error && data && data.statistics && data.statistics.time) {
                    if (state.last === LastCountEnum.LAST_WEEK) {
                        var lastWeek = data.statistics.time.timeRecordedInPeriod.lastWeek;
                        months = lastWeek.timesheetTimeGrouping.map((s) => s.group);
                        billed = lastWeek.timesheetTimeGrouping.map((s) => s.billed);
                        unbilled = lastWeek.timesheetTimeGrouping.map((s) => s.unbilled);
                        valueAmounts = lastWeek.timesheetTimeGrouping.map((s) => s.valueAmount);
                        currentTotal = lastWeek.lastPeriodTotals.periodTotal;
                        billedTotal = lastWeek.lastPeriodTotals.billedTotal;
                        unbilledTotal = lastWeek.lastPeriodTotals.unbilledTotal;
                        previousTotal = lastWeek.summary.lastPeriodTotal;
                        periodString = 'This Week';
                        previousPeroidString = 'Week';
                    } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                        var last14Days = data.statistics.time.timeRecordedInPeriod.last14Days;
                        months = last14Days.timesheetTimeGrouping.map((s) => s.group);
                        billed = last14Days.timesheetTimeGrouping.map((s) => s.billed);
                        unbilled = last14Days.timesheetTimeGrouping.map((s) => s.unbilled);
                        valueAmounts = last14Days.timesheetTimeGrouping.map((s) => s.valueAmount);
                        currentTotal = last14Days.lastPeriodTotals.periodTotal;
                        billedTotal = last14Days.lastPeriodTotals.billedTotal;
                        unbilledTotal = last14Days.lastPeriodTotals.unbilledTotal;
                        previousTotal = last14Days.summary.lastPeriodTotal;
                        periodString = 'in Last 14 Days';
                        previousPeroidString = '14 Days';
                    } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                        var last30Days = data.statistics.time.timeRecordedInPeriod.last30Days;
                        months = last30Days.timesheetTimeGrouping.map((s) => s.group);
                        billed = last30Days.timesheetTimeGrouping.map((s) => s.billed);
                        unbilled = last30Days.timesheetTimeGrouping.map((s) => s.unbilled);
                        valueAmounts = last30Days.timesheetTimeGrouping.map((s) => s.valueAmount);
                        billedTotal = last30Days.lastPeriodTotals.billedTotal;
                        unbilledTotal = last30Days.lastPeriodTotals.unbilledTotal;
                        currentTotal = last30Days.lastPeriodTotals.periodTotal;
                        previousTotal = last30Days.summary.lastPeriodTotal;
                        periodString = 'in Last 30 Days';
                        previousPeroidString = '30 Days';
                    } else {
                        var last12Months = data.statistics.time.timeRecordedInPeriod.last12Months;
                        months = last12Months.timesheetTimeGrouping.map((s) => s.group);
                        billed = last12Months.timesheetTimeGrouping.map((s) => s.billed);
                        unbilled = last12Months.timesheetTimeGrouping.map((s) => s.unbilled);
                        valueAmounts = last12Months.timesheetTimeGrouping.map((s) => s.valueAmount);
                        currentTotal = last12Months.lastPeriodTotals.periodTotal;
                        billedTotal = last12Months.lastPeriodTotals.billedTotal;
                        unbilledTotal = last12Months.lastPeriodTotals.unbilledTotal;
                        previousTotal = last12Months.summary.lastPeriodTotal;
                        periodString = 'in Last 12 Months';
                        previousPeroidString = '12 Months';
                    }
                }

                return {
                    ...prevState,
                    months,
                    billed,
                    unbilled,
                    valueAmounts,
                    billedTotal,
                    unbilledTotal,
                    previousTotal,
                    currentTotal,
                    periodString,
                    previousPeroidString,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [state.last, loading]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsTimesheetTimeInLastPeriodsChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isCurrent: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.last === LastCountEnum.LAST_WEEK) {
                // This Week
                searchCriterias.push({ property: 'ThisWeek', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '14' });
            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '30' });
            } else {
                searchCriterias.push({ property: '12MonthPeriod', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            }

            const title = `Drilldown from Hours Recorded ${state.periodString}`;

            props.getSelectedItem(
                searchCriterias, 
                false, 
                title,
                DrillDownType.timesheetSummary, 
                'Timesheet Lines'
            );
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.billed, state.unbilled) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.barTitleContainer}>
                <ChartHeading icon={<AccessTimeOutlinedIcon />} title={<>Hours Recorded {state.periodString}</>} />
                <div className={`${classes.totalDiv} h2`} onClick={() => handleTotalClick(true)}>
                    <AnimatedNumber value={state.currentTotal} format={(amount) => `${formatNumber(amount)} hrs`} />
                    {GetArrow(state.currentTotal, state.previousTotal)}
                </div>
                <ChartStatTiles
                    tiles={[
                        { label: 'Billable', value: formatNumber(state.billedTotal), color: RV_CHART_COLORS.primary, onClick: () => handleTotalClick(false) },
                        { label: 'Non-billable', value: formatNumber(state.unbilledTotal), color: RV_CHART_COLORS.primarySoft, onClick: () => handleTotalClick(false) },
                        { label: `Previous ${state.previousPeroidString}`, value: `${formatNumber(state.previousTotal)} hrs`, onClick: () => handleTotalClick(false) },
                    ]}
                />
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();
                            if (state.last === LastCountEnum.LAST_WEEK) {
                                searchCriterias.push({ property: 'Day', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                            }
                            // tslint:disable-next-line:no-console
                            // console.log(index);
                            // . e.g. - Apr 22, Apr 23, etc
                            // searchCriterias.push(
                            //     { property: 'Amount', operator: index === 0 ? 'ne' : 'eq', comparison: '0' }
                            // );

                            const title = `Drilldown from Hours Recorded ${state.periodString} - ${item}`;

                            props.getSelectedItem(searchCriterias, false, title, DrillDownType.timesheetSummary, 'Timesheet Lines');
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Billable Time',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.billed,
                                type: 'bar',
                            },
                            {
                                fill: false,
                                label: 'Non-Billable Time',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft, true),
                                data: state.unbilled,
                                type: 'bar',
                            },
                            // tslint:disable-next-line: no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                stacked: true,
                                suggestedMax: getSuggestedMaxTick([...state.billed, ...state.unbilled]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.billed, ...state.unbilled]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)} hrs`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                                // min: 0,
                                // max: 9 // minimum display
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${formatNumber(currentValue, 2)} hrs`;
                                    },
                                    // tslint:disable-next-line: no-any
                                    afterLabel: function (context: any) {
                                        const {
                                            dataIndex,
                                            //   label,
                                            // formattedValue,
                                            // dataset,
                                        } = context;

                                        const feeAmount = state.valueAmounts[dataIndex];
                                        const _billed = state.billed[dataIndex];

                                        if (_billed === 0) {
                                            return [];
                                        }
                                        return [
                                            // `Total Debt: ${symbol}${formatNumber(state.feesBilledData[tooltipItem.index])}`,
                                            `Fee Amount: ${state.symbol}${formatNumber(feeAmount, 2)}`,
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.billed, state.unbilled) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.LAST_WEEK} control={<Radio color="primary" />} label="This Week" />

                    <FormControlLabel value={LastCountEnum.LAST_14_DAYS} control={<Radio color="primary" />} label="Last 14 days" />
                    <FormControlLabel value={LastCountEnum.LAST_30_DAYS} control={<Radio color="primary" />} label="Last 30 days" />
                    <FormControlLabel value={LastCountEnum.LAST_12_MOTNHS} control={<Radio color="primary" />} label="Last 12 months" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const ChartData = gql`
    fragment LastDays on TimesheetTimeInPeriod {
        lastPeriodTotals {
            periodTotal
            billedTotal
            unbilledTotal
        }
        summary {
            lastPeriodTotal
        }
        timesheetTimeGrouping {
            billed
            unbilled
            group
            valueAmount
        }
    }

    query TimeRecordedInPeriodChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                timeRecordedInPeriod(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    last14Days {
                        ...LastDays
                    }
                    last30Days {
                        ...LastDays
                    }
                    last12Months {
                        ...LastDays
                    }
                    lastWeek {
                        ...LastDays
                    }
                }
            }
        }
    }
`;
